import React, { useContext } from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Link
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import moment from 'moment';
import { ThemeContext } from 'src/store/ContextStore';
import {
  paleChipSx,
  severityFromInterpretation
} from 'src/components/UI/chips/paleChipSx';
import { VARIANT_INTERPRETATION_OPTIONS } from '../clinical-notes-section/forms/formService';
import { TILE_COLOURS } from '../tileColours';

const HGVS_FIELDS = [
  { key: 'variantG', prefix: 'g.' },
  { key: 'variantC', prefix: 'c.' },
  { key: 'variantP', prefix: 'p.' }
];

const renderHgvs = (variant) => {
  const parts = HGVS_FIELDS.map(({ key, prefix }) => {
    const raw = variant[key];
    if (!raw) return null;
    return raw.startsWith(prefix) ? raw : `${prefix}${raw}`;
  }).filter(Boolean);

  if (parts.length === 0) return variant.gene || 'Unspecified variant';
  return parts.join(' · ');
};

const VariantRow = ({
  variant,
  onClinicianInterpretationChange,
  onSourceTestClick,
  disabled
}) => {
  const { theme } = useContext(ThemeContext);
  const dateFormat = theme?.dateFormat || 'DD/MM/YYYY';

  const labSeverity = severityFromInterpretation(variant.labInterpretation);
  const clinvarSeverity = severityFromInterpretation(
    variant.clinvarInterpretation
  );
  const showDiscordance =
    variant.labInterpretation &&
    variant.clinvarInterpretation &&
    labSeverity !== clinvarSeverity;

  const sourceLabel = [
    variant.sourceTestName,
    variant.sourceTestDate
      ? moment(variant.sourceTestDate).format(dateFormat)
      : ''
  ]
    .filter(Boolean)
    .join(' – ');

  return (
    <Box
      sx={{
        border: `1px solid ${TILE_COLOURS.genotype.border}`,
        borderLeft: `3px solid ${TILE_COLOURS.genotype.accent}`,
        backgroundColor: TILE_COLOURS.genotype.background,
        borderRadius: '6px',
        px: 1.5,
        py: 1,
        mb: 1
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: TILE_COLOURS.genotype.text,
          lineHeight: 1
        }}
      >
        Variant
      </Typography>

      <Typography
        sx={{
          mt: 0.5,
          fontFamily: 'monospace',
          fontSize: 13,
          wordBreak: 'break-word'
        }}
      >
        {renderHgvs(variant)}
      </Typography>

      {sourceLabel && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}
        >
          From:{' '}
          {variant.sourceTestId ? (
            <Link
              component="button"
              type="button"
              underline="always"
              variant="caption"
              onClick={() => {
                const testId = String(variant.sourceTestId);
                if (onSourceTestClick) {
                  onSourceTestClick({ testId });
                  return;
                }
                window.dispatchEvent(
                  new CustomEvent('open-source-test-record', {
                    detail: { testId }
                  })
                );
              }}
              sx={{ verticalAlign: 'baseline', fontWeight: 500 }}
            >
              {sourceLabel}
            </Link>
          ) : (
            sourceLabel
          )}
        </Typography>
      )}

      <Box
        sx={{
          mt: 1,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.75
        }}
      >
        {variant.labInterpretation && (
          <Chip
            size="small"
            label={`Lab: ${variant.labInterpretation}`}
            sx={paleChipSx(labSeverity)}
          />
        )}

        {variant.clinvarInterpretation ? (
          variant.clinvarVariationId ? (
            <Tooltip
              title={
                <Box sx={{ fontSize: 12, lineHeight: 1.4 }}>
                  {variant.clinvarReviewStatus && (
                    <Box>
                      <strong>Review status:</strong>{' '}
                      {variant.clinvarReviewStatus}
                    </Box>
                  )}
                  {variant.clinvarLastEvaluated && (
                    <Box>
                      <strong>Last evaluated:</strong>{' '}
                      {variant.clinvarLastEvaluated}
                    </Box>
                  )}
                  <Box sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    Click to open in ClinVar
                  </Box>
                </Box>
              }
            >
              <a
                href={`https://www.ncbi.nlm.nih.gov/clinvar/variation/${variant.clinvarVariationId}/`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: 'none',
                  display: 'inline-flex'
                }}
              >
                <Chip
                  size="small"
                  label={`ClinVar: ${variant.clinvarInterpretation}`}
                  sx={[paleChipSx(clinvarSeverity), { cursor: 'pointer' }]}
                />
              </a>
            </Tooltip>
          ) : (
            <Chip
              size="small"
              label={`ClinVar: ${variant.clinvarInterpretation}`}
              sx={paleChipSx(clinvarSeverity)}
            />
          )
        ) : (
          variant.clinvarLastFetchedAt && (
            <Chip
              size="small"
              label="ClinVar: No match"
              sx={paleChipSx('unclassified')}
            />
          )
        )}

        <ClinicianSelect
          value={variant.clinicianInterpretation || ''}
          disabled={disabled}
          onChange={(value) =>
            onClinicianInterpretationChange(variant.variantId, value)
          }
        />

        {showDiscordance && (
          <Tooltip title="Lab and ClinVar interpretations disagree">
            <WarningAmberIcon color="warning" fontSize="small" />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const ClinicianSelect = ({ value, onChange, disabled }) => {
  const severity = severityFromInterpretation(value);

  return (
    <FormControl size="small" disabled={disabled}>
      <Select
        displayEmpty
        value={value}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(v) => `Clinician: ${v || 'None'}`}
        sx={(theme) => ({
          height: 24,
          fontSize: 12,
          ...paleChipSx(severity)(theme),
          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& .MuiSelect-select': {
            py: 0,
            pl: 1,
            pr: '24px !important',
            display: 'flex',
            alignItems: 'center'
          }
        })}
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        {VARIANT_INTERPRETATION_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default VariantRow;
